import GridFormLabel from 'cl-form/GridFormLabel';
import * as React from 'react';
import * as Papa from 'papaparse';
import * as _ from 'lodash';
import {ColumnsType} from 'cl-table/redux/actions';
import FormDialog from 'cl-dialogs/FormDialog/FormDialog';
import {GENERAL_ACTIONS_TYPES} from '../../../ActionsManager/ActionsTypes';
import GeneralActions from '../../../ActionsManager/GeneralActions/GeneralActions';
import {ACTION_DISPLAY_TYPES} from '../../../ActionsManager/ActionsManagerConsts';
import FormFile from 'cl-form/FormFile/FormFile';
import InlineButton from '../../InlineButton';
import {formValueSelector} from 'redux-form';
import * as Fuse from 'fuse.js';
import {SpecificFormDialogProps} from 'cl-dialogs/FormDialog';
import FormRadio, {RadioBoolean} from 'cl-form/FormRadio/FormRadio';
import TooltipIcon from '@material-ui/core/TooltipIcon/TooltipIcon';
import createConditional from 'cl-form/createConditional';
import FormCheckbox from 'cl-form/FormCheckbox/FormCheckbox';
import {ExtendedWrappedFieldExternalProps, ExtendedWrappedFieldProps} from 'cl-form/FormFields';
import Grow from '@material-ui/core/Grow';
import {FormSelectOptions} from 'cl-form/utils/orderByUtils';
import FormSelect from 'cl-form/FormSelect/FormSelect';
import {createValidateRequired} from 'cl-form/validation/validators';
import GridFormHeader from 'cl-form/GridFormHeader';
import {useSelector} from 'react-redux';
import {Omit} from '@material-ui/core';
import {Tables} from 'cl-api';
import {store} from '../../../../App/store';
import {addDynamicDialog} from '../../../../crazyEditor/redux/modules/panel/panel';
import CreateAttributeDialog from '../CreateAttributeDialog/CreateAttributeDialog';

type AttributeMapping = {[mappedProp: string]: string};

type Step1FormData = {
  file: File;
  updateExisting: RadioBoolean;
  updateOnlyEmptyAttributes: boolean;
  replaceImages: boolean;
  ignoreBlankAttributes: boolean;
};
export type ImportCsvFormData = Step1FormData & AttributeMapping;

export interface SourceAttribute {
  name: string;
  example: string;
}

export interface ImportCsvDialogProps
  extends Pick<Step1FormData, 'file'>,
    Omit<SpecificFormDialogProps<ImportCsvFormData>, 'variant'> {
  entityName: string;
  variant?: 'import' | 'update';
  supportImages?: boolean;
  supportUpdateEmptyAttributes?: boolean;
  sampleCSVHref?: string;
  activityTask?: string;
  destinationAttributes: ColumnsType;
  attributesColumns: any;
}

const ImportCsvDialog: React.FunctionComponent<ImportCsvDialogProps> = ({
  entityName,
  variant,
  supportImages,
  supportUpdateEmptyAttributes,
  sampleCSVHref,
  activityTask,
  destinationAttributes,
  initialValues,
  formName,
  attributesColumns,
  ...props
}) => {
  const [defaultMapping, SetDefaultMapping] = React.useState<AttributeMapping>({});
  const [sourceAttributes, SetSourceAttributes] = React.useState<SourceAttribute[]>(null);
  const getFormValues = React.useMemo(() => formValueSelector(formName), [formName]);
  const file = useSelector(state => getFormValues(state, 'file'));

  React.useEffect(() => {
    if (file) {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        preview: 1,
        complete: ({data}) => {
          const fuse = new Fuse(
            _.map(destinationAttributes, ({displayName}, name) => ({
              displayName,
              name,
            })),
            {keys: ['name', 'displayName'], shouldSort: true, threshold: 0.3}
          );
          const firstRow = _.omitBy(data[0], (value, key) => _.isEmpty(key));
          SetSourceAttributes(
            _.map(firstRow, (example, sourceAttributeName) => ({
              name: (sourceAttributeName as unknown) as string,
              example: example as string,
            }))
          );
          SetDefaultMapping(
            _.mapValues(
              firstRow,
              (value, sourceAttributeName) => (_.head(fuse.search(sourceAttributeName)) || {name: undefined}).name
            )
          );
        },
      });
    }
  }, [file]);

  const options: FormSelectOptions = React.useMemo(
    () => _.map(destinationAttributes, ({displayName}, name) => ({value: name, label: displayName || name})),
    [destinationAttributes]
  );

  const ShowWhenUpdateExisting = React.useMemo(
    () =>
      createConditional<Step1FormData, 'updateExisting'>({
        fieldNames: ['updateExisting'],
        props: {in: true},
        condition: ({updateExisting}) => variant === 'update' || updateExisting === RadioBoolean.true,
      })(props => <Grow appear={false} {...props} />),
    []
  );

  // noinspection RequiredAttributes
  const fileFieldData = React.useMemo(
    () => ({
      component: FormFile,
      name: 'file',
      label: 'pick a csv from your computer',
      instructionText: (
        <React.Fragment>
          Download a{' '}
          <InlineButton disableOpenInNewIcon target={null} href={sampleCSVHref}>
            sample CSV template
          </InlineButton>{' '}
          to see an example of the format required.
        </React.Fragment>
      ),
      instructionPosition: 'after',
    }),
    [sampleCSVHref]
  );

  // noinspection RequiredAttributes
  const updateExistingFieldData = React.useMemo(
    () => ({
      component: FormRadio,
      name: 'updateExisting',
      props: {
        options: [
          {
            value: RadioBoolean.false,
            label: (
              <React.Fragment>
                {`Don't update existing ${entityName}s `}
                <TooltipIcon
                  variant="inline"
                  title={`If checked, any existing ${entityName}s with same SKU will NOT be changed or updated.`}
                />
              </React.Fragment>
            ),
          },
          {
            value: RadioBoolean.true,
            label: (
              <React.Fragment>
                {`Update existing ${entityName}s `}
                <TooltipIcon
                  variant="inline"
                  title={`If checked, any existing ${entityName}s with same SKU will be updated with the information from the CSV file.`}
                />
              </React.Fragment>
            ),
          },
        ],
      },
    }),
    []
  );

  // noinspection RequiredAttributes
  const updateOnlyEmptyAttributesFieldData = React.useMemo(
    () => ({
      component: props => (
        <ShowWhenUpdateExisting>
          <FormCheckbox {...props} />
        </ShowWhenUpdateExisting>
      ),
      name: 'updateOnlyEmptyAttributes',
      label: (
        <React.Fragment>
          {`Update only empty attributes of existing ${entityName}s `}
          <TooltipIcon
            variant="inline"
            title={`If checked, empty attributes of existing ${entityName}s will be updated from the CSV file, and existing attribute values will not be changed or updated.`}
          />
        </React.Fragment>
      ),
      indent: 1,
    }),
    []
  );

  // noinspection RequiredAttributes
  const replaceImagesFieldData = React.useMemo(
    () => ({
      component: props => (
        <ShowWhenUpdateExisting>
          <FormCheckbox {...props} />
        </ShowWhenUpdateExisting>
      ),
      name: 'replaceImages',
      label: (
        <React.Fragment>
          {`Replace existing ${entityName} images `}
          <TooltipIcon
            variant="inline"
            title={`If checked, existing ${entityName} images will be removed and replaced with images from the CSV file. If unchecked, images from the CSV file will be added to the existing ${entityName} images.`}
          />
        </React.Fragment>
      ),
      indent: 1,
    }),
    []
  );

  // noinspection RequiredAttributes
  const ignoreBlankAttributesFieldData = React.useMemo(
    () => ({
      component: createConditional<Step1FormData, 'updateOnlyEmptyAttributes'>({
        fieldNames: ['updateOnlyEmptyAttributes'],
        condition: ({updateOnlyEmptyAttributes}) => updateOnlyEmptyAttributes,
        props: {disabled: true},
      })((props: ExtendedWrappedFieldProps) => (
        <ShowWhenUpdateExisting>
          <FormCheckbox {...props} />
        </ShowWhenUpdateExisting>
      )),
      name: 'ignoreBlankAttributes',
      label: (
        <React.Fragment>
          Ignore blank CSV cells during import{' '}
          <TooltipIcon
            variant="inline"
            title="If checked, any blank cells in your CSV file will be ignored, and the existing attribute values will not be changed or updated."
          />
        </React.Fragment>
      ),
      indent: 1,
    }),
    []
  );

  const step1Fields: ExtendedWrappedFieldExternalProps<keyof Step1FormData>[] = React.useMemo(() => {
    const stepFields: any = [fileFieldData];
    if (variant === 'import') {
      stepFields.push(updateExistingFieldData);
    }
    if (supportUpdateEmptyAttributes) {
      stepFields.push(updateOnlyEmptyAttributesFieldData);
    }
    if (supportImages) {
      stepFields.push(replaceImagesFieldData);
    }
    stepFields.push(ignoreBlankAttributesFieldData);
    return stepFields;
  }, [variant, supportUpdateEmptyAttributes, supportImages]);

  const mapFieldsStepName = React.useMemo(() => `Map imported fields to ${entityName} attributes`, [entityName]);

  return (
    <FormDialog<ImportCsvFormData>
      enableReinitialize
      keepDirtyOnReinitialize
      fullWidth
      formName={formName}
      title={`${_.upperFirst(variant)} ${entityName}s using CSV file`}
      initialValues={
        {
          updateExisting: variant === 'import' ? RadioBoolean.false : RadioBoolean.true,
          ignoreBlankAttributes: true,
          ...initialValues,
          ...defaultMapping,
          file,
        } as Partial<ImportCsvFormData>
      }
      fields={{
        'Upload files': step1Fields,
        [mapFieldsStepName]: [
          {
            component: GridFormHeader,
            name: 'header',
            props: {leftText: 'Imported fields', rightText: `${entityName} attributes`},
          },
          ..._.map(sourceAttributes, ({name, example}) => ({
            component: GridFormLabel,
            label: name,
            name,
            explanationText: example,
            props: {
              inputComponent: FormSelect,
              options: options,
              stickyActions: [
                {
                  ...{
                    onClick: handleDestinationAttributeChange => {
                      store.dispatch(
                        addDynamicDialog(CreateAttributeDialog, {
                          columns: attributesColumns,
                          createAttributeFromCsvProps: {defaultAttributeName: name, handleDestinationAttributeChange},
                        })
                      );
                    },
                  },
                  label: 'Create new attribute',
                },
                {
                  ...{
                    onClick: handleDestinationAttributeChange => {
                      handleDestinationAttributeChange({
                        value: 'N/A',
                        label: "Don't import",
                      });
                    },
                  },
                  label: `Don't Import`,
                },
              ],
            },
          })),
        ],
      }}
      steps={[
        {
          name: 'Upload files',
          validate: createValidateRequired([step1Fields[0]]),
        },
        {name: mapFieldsStepName, validate: null},
      ]}
      actions={
        <GeneralActions
          types={[GENERAL_ACTIONS_TYPES.IMPORT_INVENTORY_CSV]}
          displayType={ACTION_DISPLAY_TYPES.FLAT_BUTTON}
          lastActionColor="primary"
          additionalActionData={{task: activityTask}}
        />
      }
      {...props}
    />
  );
};

ImportCsvDialog.defaultProps = {
  variant: 'import',
  entityName: 'product',
  formName: GENERAL_ACTIONS_TYPES.IMPORT_INVENTORY_CSV,
  supportImages: true,
  supportUpdateEmptyAttributes: true,
  sampleCSVHref: 'https://misc.crazylister.com/file-examples/products.csv',
  activityTask: Tables.InventoryTable as any,
};

export default ImportCsvDialog;
